/**
 * @fileoverview Hook to retrieve top revenue-generating countries per period (TIME) for tooltips.
 * @module shared/hooks/dashboard/useTopCountryRevenueByPeriod
 */

import { useCallback, useContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FilterContext } from '@/shared/context/FilterContext'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { statisticsService, type TimeAggregation } from '@/lib/statisticsService'
import { prefixWithOrg, queryKeys } from '@/shared/utils/queryKeys'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'
import { useLabelResolver } from '@/shared/hooks/useLabelResolver'

export interface PeriodCountryRow {
  country: string
  name: string
  revenue: number
  adSpend: number
  roas: number
}

export interface UseTopCountryRevenueByPeriodResult {
  byPeriod: Map<string, PeriodCountryRow[]>
  topCountryRevenueFor: (period: string, limit?: number) => PeriodCountryRow[]
  totalRevenueFor: (period: string) => number
  totalAdSpendFor: (period: string) => number
  isLoading: boolean
  isError: boolean
  error: string | null
}

function useFilterParams(): { filter: Record<string, unknown>; timeAggregation: TimeAggregation } {
  const ctx = useContext(FilterContext)
  if (!ctx) throw new Error('useTopCountryRevenueByPeriod must be used within FilterProvider')

  const { startDate, endDate, aggregation, countries, skus, tags } = ctx
  const filter: Record<string, unknown> = {}
  if (startDate) filter.startDate = toDateOnlyStringLocal(startDate)
  if (endDate) filter.endDate = toDateOnlyStringLocal(endDate)
  if (countries.length > 0) filter.countries = countries
  if (skus.length > 0) filter.skus = skus
  if (tags.length > 0) filter.tags = tags

  let timeAggregation: TimeAggregation = 'DAILY'
  if (aggregation) {
    const map = {
      Daily: 'DAILY',
      Weekly: 'WEEKLY',
      Monthly: 'MONTHLY',
      Quarterly: 'QUARTERLY',
      Yearly: 'YEARLY',
    } as const
    timeAggregation = map[aggregation]
  }

  return { filter, timeAggregation }
}

export function useTopCountryRevenueByPeriod(): UseTopCountryRevenueByPeriodResult {
  const { countryLabel } = useLabelResolver()
  const { activeOrgId } = useActiveOrg()
  const { filter, timeAggregation } = useFilterParams()

  const query = useQuery({
    queryKey: activeOrgId
      ? prefixWithOrg(
          activeOrgId as unknown as string,
          queryKeys.salesStatistics(filter, ['TIME', 'COUNTRY'], timeAggregation)
        )
      : queryKeys.salesStatistics(filter, ['TIME', 'COUNTRY'], timeAggregation),
    queryFn: () =>
      statisticsService.getSalesStatistics(filter, ['TIME', 'COUNTRY'], timeAggregation),
    enabled: Boolean(activeOrgId && filter && filter['startDate'] && filter['endDate']),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const byPeriod = useMemo(() => {
    const map = new Map<string, PeriodCountryRow[]>()
    const rows = query.data?.data || []

    rows.forEach(row => {
      const dims = row.dimensions as Record<string, unknown> | undefined
      const timeKey = (dims?.timePeriod as string) || ''
      const country = (dims?.country as string) || ''
      if (!timeKey || !country) return

      const m = row.metrics as Record<string, number | undefined> | undefined
      const revenue = (m?.sales as number) || 0
      const adSpend = (m?.advertising as number) || 0
      const roas = revenue > 0 && adSpend > 0 ? revenue / adSpend : 0

      const list = map.get(timeKey) || []
      const existingIndex = list.findIndex(p => p.country === country)
      if (existingIndex >= 0) {
        const existing = list[existingIndex]!
        existing.revenue += revenue
        existing.adSpend += adSpend
        const combinedAd = existing.adSpend
        existing.roas =
          combinedAd > 0 ? Math.max(0, existing.revenue) / Math.max(1e-9, combinedAd) : 0
      } else {
        list.push({
          country,
          name: countryLabel(country),
          revenue,
          adSpend,
          roas,
        })
      }
      map.set(timeKey, list)
    })

    for (const [, list] of map.entries()) {
      list.sort((a, b) => b.revenue - a.revenue)
    }

    return map
  }, [query.data, countryLabel])

  const topCountryRevenueFor = useCallback(
    (period: string, limit = 6): PeriodCountryRow[] => {
      const list = byPeriod.get(period) || []
      return list.filter(p => p.revenue > 0).slice(0, limit)
    },
    [byPeriod]
  )

  const totalRevenueFor = useCallback(
    (period: string): number => {
      const list = byPeriod.get(period) || []
      return list.reduce((sum, p) => sum + p.revenue, 0)
    },
    [byPeriod]
  )

  const totalAdSpendFor = useCallback(
    (period: string): number => {
      const list = byPeriod.get(period) || []
      return list.reduce((sum, p) => sum + p.adSpend, 0)
    },
    [byPeriod]
  )

  return {
    byPeriod,
    topCountryRevenueFor,
    totalRevenueFor,
    totalAdSpendFor,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error?.message || null,
  }
}
